import { api } from '../../features/auth/authStore';
import { mockCalendarData, mockAttendanceStats, mockAttendanceActions } from '../mockData';

// Types
export interface ClockRequest {
  userId?: string;
  time?: string;
  source: 'web' | 'mobile' | 'biometric' | 'manual';
  notes?: string;
}

export interface AttendanceRecord {
  id: string;
  userId: string;
  day: string;
  clockIns: string[];
  clockOuts: string[];
  totalMinutes: number;
  isComplete: boolean;
  source: string;
  notes?: string;
  createdAt: string;
  updatedAt: string;
}

export interface AttendanceStatus {
  isActive: boolean;
  currentSession?: {
    clockInTime: string;
    duration: number;
  };
  todayTotal: number;
  canClockIn: boolean;
  canClockOut: boolean;
}

export interface AttendanceListQuery {
  page?: number;
  limit?: number;
  userId?: string;
  startDate?: string;
  endDate?: string;
  source?: string;
  isComplete?: boolean;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

export interface AttendanceListResponse {
  attendance: AttendanceRecord[];
  total: number;
  page: number;
  limit: number;
}

export interface BiometricImportRequest {
  data: Array<{
    userId: string;
    clockIns: string[];
    clockOuts: string[];
    day: string;
  }>;
  importBatchId: string;
}

export interface CalendarDay {
  date: string;
  status: 'present' | 'absent' | 'leave' | 'holiday' | 'weekend' | 'partial' | 'future';
  attendance?: {
    clockIns: string[];
    clockOuts: string[];
    totalMinutes: number;
    isComplete: boolean;
  };
  leave?: {
    leaveType: string;
    leaveTypeColor: string;
    reason: string;
    status: string;
  };
  holiday?: {
    name: string;
    type: string;
    color: string;
  };
  isWorkingDay: boolean;
  displayText: string;
}

export const attendanceApi = {
  // Clock in
  clockIn: async (data: ClockRequest): Promise<AttendanceRecord> => {
    console.log('🔄 Calling clockIn API with data:', data);
    const response = await api.post('/attendance/clock-in', data);
    console.log('✅ clockIn API response:', response.data);
    return response.data;
  },

  // Clock out
  clockOut: async (data: ClockRequest): Promise<AttendanceRecord> => {
    console.log('🔄 Calling clockOut API with data:', data);
    const response = await api.post('/attendance/clock-out', data);
    console.log('✅ clockOut API response:', response.data);
    return response.data;
  },

  // Enhanced clock in with validation
  clockInEnhanced: async (data: ClockRequest): Promise<AttendanceRecord> => {
    console.log('🔄 Calling clockInEnhanced API with data:', data);
    const response = await api.post('/attendance/clock-in-enhanced', data);
    console.log('✅ clockInEnhanced API response:', response.data);

    // Handle backend response structure
    if (response.data && response.data.success && response.data.data) {
      return response.data.data;
    }
    // If response doesn't have expected structure, return the data directly
    return response.data || response;
  },

  // Enhanced clock out with validation
  clockOutEnhanced: async (data: ClockRequest): Promise<AttendanceRecord> => {
    console.log('🔄 Calling clockOutEnhanced API with data:', data);
    const response = await api.post('/attendance/clock-out-enhanced', data);
    console.log('✅ clockOutEnhanced API response:', response.data);

    // Handle backend response structure
    if (response.data && response.data.success && response.data.data) {
      return response.data.data;
    }
    // If response doesn't have expected structure, return the data directly
    return response.data || response;
  },

  // Check if user can clock in/out
  canClock: async (params?: { date?: string; userId?: string }): Promise<{
    canClock: boolean;
    reason?: string;
    isHoliday?: boolean;
    isOnLeave?: boolean;
  }> => {
    console.log('🔄 Calling canClock API with params:', params);
    const response = await api.get('/attendance/can-clock', { params });
    console.log('✅ canClock API response:', response.data);
    return response.data;
  },

  // Get attendance status for specific date
  getAttendanceStatus: async (date: string, params?: { userId?: string }): Promise<{
    status: 'present' | 'absent' | 'leave' | 'holiday' | 'weekend' | 'partial';
    attendance?: AttendanceRecord;
    leave?: any;
    holiday?: any;
    canClock: boolean;
  }> => {
    const response = await api.get(`/attendance/status/${date}`, { params });
    return response.data;
  },

  // Get comprehensive attendance data
  getComprehensiveAttendance: async (params?: AttendanceListQuery & {
    includeLeaveData?: boolean;
    includeHolidayData?: boolean;
  }): Promise<{
    attendance: AttendanceRecord[];
    leaves: any[];
    holidays: any[];
    total: number;
    page: number;
    limit: number;
  }> => {
    const response = await api.get('/attendance/comprehensive', { params });
    return response.data;
  },

  // Get current status
  getCurrentStatus: async (): Promise<AttendanceStatus> => {
    console.log('🔄 Calling getCurrentStatus API...');
    const response = await api.get('/attendance/status');
    console.log('✅ getCurrentStatus API response:', response.data);

    // Handle backend response structure
    if (response.data && response.data.success && response.data.data) {
      const backendData = response.data.data;
      return {
        isActive: backendData.isClockedIn || false,
        currentSession: backendData.isClockedIn ? {
          clockInTime: backendData.lastClockIn || new Date().toISOString(),
          duration: backendData.todayMinutes || 0,
        } : undefined,
        todayTotal: backendData.todayMinutes || 0,
        canClockIn: !backendData.isClockedIn,
        canClockOut: backendData.isClockedIn,
      };
    }
    return response.data;
  },

  // Get attendance records
  getAttendanceRecords: async (params?: AttendanceListQuery): Promise<AttendanceListResponse> => {
    console.log('🔄 Calling getAttendanceRecords API with params:', params);
    const response = await api.get('/attendance', { params });
    console.log('✅ getAttendanceRecords API response:', response.data);
    return response.data;
  },

  // Get attendance record by ID
  getAttendanceRecord: async (id: string): Promise<AttendanceRecord> => {
    const response = await api.get(`/attendance/${id}`);
    return response.data;
  },

  // Update attendance record
  updateAttendanceRecord: async (id: string, data: Partial<AttendanceRecord>): Promise<AttendanceRecord> => {
    const response = await api.put(`/attendance/${id}`, data);
    return response.data;
  },

  // Delete attendance record
  deleteAttendanceRecord: async (id: string): Promise<void> => {
    await api.delete(`/attendance/${id}`);
  },

  // Import biometric data
  importBiometricData: async (data: BiometricImportRequest): Promise<AttendanceRecord[]> => {
    const response = await api.post('/attendance/import/biometric', data);
    return response.data;
  },

  // Get attendance calendar
  getAttendanceCalendar: async (params: {
    userId?: string;
    startDate: string;
    endDate: string;
    includeWeekends?: boolean;
  }): Promise<{
    calendar: CalendarDay[];
    summary: {
      totalDays: number;
      workingDays: number;
      presentDays: number;
      absentDays: number;
      leaveDays: number;
      holidayDays: number;
    };
  }> => {
    try {
      console.log('🔄 Calling attendance calendar API with params:', params);
      const response = await api.get('/leaves/calendar', { params });
      console.log('✅ Calendar API response:', response.data);

      // Ensure the response has the expected structure
      if (response.data?.data) {
        return response.data.data;
      } else if (response.data?.calendar) {
        return response.data;
      } else {
        throw new Error('Invalid response structure from calendar API');
      }
    } catch (error) {
      console.error('❌ Error fetching attendance calendar:', error);
      throw error;
    }
  },

  // Get attendance summary
  getAttendanceSummary: async (params: {
    userId?: string;
    startDate: string;
    endDate: string;
  }): Promise<{
    totalWorkingDays: number;
    presentDays: number;
    absentDays: number;
    leaveDays: number;
    holidayDays: number;
    partialDays: number;
    totalWorkingHours: number;
    averageWorkingHours: number;
  }> => {
    const response = await api.get('/leaves/summary', { params });
    return response.data;
  },

  // Get monthly attendance overview
  getMonthlyAttendanceOverview: async (year: number, month: number, params?: { userId?: string }): Promise<{
    year: number;
    month: number;
    calendar: CalendarDay[];
    summary: any;
    monthName: string;
  }> => {
    const response = await api.get(`/leaves/monthly/${year}/${month}`, { params });
    return response.data;
  },

  // Get attendance statistics
  getAttendanceStats: async (params?: { userId?: string }): Promise<{
    monthly: any;
    weekly: any;
    period: {
      monthStart: string;
      weekStart: string;
      current: string;
    };
  }> => {
    console.log('🔄 Calling attendance stats API with params:', params);
    const response = await api.get('/leaves/stats', { params });
    console.log('✅ Stats API response:', response.data);

    // Ensure the response has the expected structure
    if (response.data?.data) {
      return response.data.data;
    } else if (response.data?.monthly || response.data?.weekly) {
      return response.data;
    } else {
      throw new Error('Invalid response structure from stats API');
    }
  },

  // Get attendance report
  getAttendanceReport: async (params: {
    startDate?: string;
    endDate?: string;
    format?: string;
    _cacheBuster?: string;
  }): Promise<{
    reportId: string;
    reportType: string;
    title: string;
    generatedAt: string;
    dateRange: {
      startDate: string;
      endDate: string;
    };
    period: {
      startDate: string;
      endDate: string;
    };
    summary: {
      totalWorkingDays: number;
      averageAttendanceRate: number;
      totalWorkingHours: number;
      averageHoursPerDay: number;
    };
    byStaff: Array<{
      userId: string;
      staffName: string;
      workingDays: number;
      attendanceRate: number;
      totalHours: number;
      averageHoursPerDay: number;
      lateArrivals: number;
      earlyDepartures: number;
    }>;
    trends: Array<{
      date: string;
      attendanceRate: number;
      totalHours: number;
      staffCount: number;
    }>;
  }> => {
    console.log('🔄 Calling attendance report API with params:', params);

    // Build params object - only include dates if provided (for all data, don't send dates)
    const apiParams: any = {
      format: params.format || 'json'
    };

    // Add cache buster if provided
    if (params._cacheBuster) {
      apiParams._cacheBuster = params._cacheBuster;
    }

    // Only add date filters if they are provided
    if (params.startDate && params.endDate) {
      apiParams.startDate = params.startDate;
      apiParams.endDate = params.endDate;
      console.log('📅 Using date filter:', { startDate: params.startDate, endDate: params.endDate });
    } else {
      console.log('📊 Fetching ALL attendance data (no date filter) - Cache buster:', params._cacheBuster);
    }

    const response = await api.get('/firm-admin/reports/attendance', {
      params: apiParams
    });
    console.log('✅ Attendance report API response:', response.data);

    if (response.data?.success && response.data?.data) {
      return response.data.data;
    } else {
      throw new Error('Invalid response structure from attendance report API');
    }
  },

  // Generate attendance report (alternative endpoint)
  generateAttendanceReport: async (params: {
    startDate: string;
    endDate: string;
    format?: string;
  }): Promise<any> => {
    console.log('🔄 Calling generate attendance report API with params:', params);
    const response = await api.get('/firm-admin/reports/generate', {
      params: {
        reportType: 'attendance',
        startDate: params.startDate,
        endDate: params.endDate,
        format: params.format || 'json'
      }
    });
    console.log('✅ Generate attendance report API response:', response.data);

    if (response.data?.success && response.data?.data) {
      return response.data.data;
    } else {
      throw new Error('Invalid response structure from generate attendance report API');
    }
  },

  // Export attendance report with proper format handling
  exportAttendanceReport: async (params: {
    startDate?: string;
    endDate?: string;
    format: 'pdf' | 'excel' | 'csv' | 'json';
    _cacheBuster?: string;
  }): Promise<any> => {
    console.log('🔄 Calling export attendance report API with params:', params);

    // Build params object - only include dates if provided (for all data, don't send dates)
    const apiParams: any = {
      format: params.format // Keep format as-is (pdf, excel, csv, json)
    };

    // Add cache buster if provided
    if (params._cacheBuster) {
      apiParams._cacheBuster = params._cacheBuster;
    }

    // Only add date filters if they are provided
    if (params.startDate && params.endDate) {
      apiParams.startDate = params.startDate;
      apiParams.endDate = params.endDate;
      console.log('📅 Exporting with date filter:', { startDate: params.startDate, endDate: params.endDate });
    } else {
      console.log('📊 Exporting ALL attendance data (no date filter) - Cache buster:', params._cacheBuster);
    }

    // Use the specific attendance endpoint (same pattern as financial reports)
    const response = await api.get('/firm-admin/reports/attendance', {
      params: apiParams,
      responseType: params.format === 'json' ? 'json' : 'blob'
    });

    console.log('✅ Export attendance report API response:', response.data);

    // Handle different response types
    if (params.format === 'json') {
      // For JSON format, handle the structured response
      if (response.data?.success && response.data?.data) {
        // Download JSON data
        const blob = new Blob([JSON.stringify(response.data.data, null, 2)], {
          type: 'application/json'
        });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        const dateStr = params.startDate && params.endDate
          ? `${params.startDate}-to-${params.endDate}`
          : 'all-data';
        link.download = `attendance-report-${dateStr}.json`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        return response.data.data;
      }
    } else {
      // For PDF, Excel, CSV - response is a blob, download it directly
      const mimeTypes = {
        pdf: 'application/pdf',
        excel: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        csv: 'text/csv'
      };

      const extensions = {
        pdf: 'pdf',
        excel: 'xlsx',
        csv: 'csv'
      };

      const blob = new Blob([response.data], {
        type: mimeTypes[params.format as keyof typeof mimeTypes]
      });

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `attendance-report-${params.startDate}-to-${params.endDate}.${extensions[params.format as keyof typeof extensions]}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      return { success: true };
    }

    throw new Error('Invalid response structure from export attendance report API');
  },
};
